import React from 'react';
import { useSelector } from 'react-redux';
import { getFileStats } from '../../../../../helpers/file-helper';

const formatBytes = (value = 0) => {
    const bytes = Number(value || 0);
    if (!Number.isFinite(bytes) || bytes <= 0) {
        return '0 B';
    }

    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    const scaled = bytes / Math.pow(1024, index);

    return `${scaled.toFixed(scaled >= 10 || index === 0 ? 0 : 2)} ${units[index]}`;
};

export const AccountStorageTabContent = () => {
    const { token } = useSelector((state) => state.auth);

    const [isLoading, setIsLoading] = React.useState(true);
    const [error, setError] = React.useState('');
    const [stats, setStats] = React.useState({
        totalFiles: 0,
        totalBytes: 0,
        maxStorage: 0,
        usagePercent: null,
        byMimeType: [],
    });

    React.useEffect(() => {
        const loadStats = async () => {
            if (!token) {
                setError('No se pudo validar la sesión.');
                setIsLoading(false);
                return;
            }

            setIsLoading(true);
            setError('');

            const resp = await getFileStats(token);
            if (!resp?.success) {
                setError(resp?.message || 'No se pudieron obtener las métricas de almacenamiento.');
                setIsLoading(false);
                return;
            }

            const payload = resp?.data || {};
            setStats({
                totalFiles: Number(payload.totalFiles || 0),
                totalBytes: Number(payload.totalBytes || 0),
                maxStorage: Number(payload.maxStorage || 0),
                usagePercent: payload.usagePercent === null || payload.usagePercent === undefined
                    ? null
                    : Number(payload.usagePercent || 0),
                byMimeType: Array.isArray(payload.byMimeType) ? payload.byMimeType : [],
            });
            setIsLoading(false);
        };

        loadStats();
    }, [token]);

    const usagePercent = stats.usagePercent === null
        ? 0
        : Math.max(0, Math.min(100, Number(stats.usagePercent || 0)));

    const usageBarClass = usagePercent >= 90
        ? 'bg-danger'
        : usagePercent >= 75
            ? 'bg-warning'
            : 'bg-success';

    return (
        <div>
            <div className="mb-4">
                <h6 className="fw-semibold mb-1">Almacenamiento del tenant</h6>
                <p className="text-muted fs-12 mb-0">Resumen del uso de archivos asociados a tu compañía.</p>
            </div>

            {error && (
                <div className="alert alert-danger py-2" role="alert">{error}</div>
            )}

            {isLoading ? (
                <div className="text-muted">Cargando métricas de almacenamiento...</div>
            ) : (
                <>
                    <div className="row g-3 mb-4">
                        <div className="col-md-4">
                            <div className="border border-dashed border-gray-5 rounded-2 p-3 h-100">
                                <div className="text-muted fs-12 mb-1">Archivos activos</div>
                                <div className="fs-4 fw-bold">{stats.totalFiles}</div>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="border border-dashed border-gray-5 rounded-2 p-3 h-100">
                                <div className="text-muted fs-12 mb-1">Uso actual</div>
                                <div className="fs-4 fw-bold">{formatBytes(stats.totalBytes)}</div>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="border border-dashed border-gray-5 rounded-2 p-3 h-100">
                                <div className="text-muted fs-12 mb-1">Límite configurado</div>
                                <div className="fs-4 fw-bold">
                                    {stats.maxStorage > 0 ? formatBytes(stats.maxStorage) : 'Sin límite'}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="mb-4">
                        <div className="d-flex align-items-center justify-content-between mb-2">
                            <span className="text-muted fs-12">Porcentaje de uso</span>
                            <span className="fw-semibold">{stats.usagePercent === null ? 'N/A' : `${usagePercent.toFixed(2)}%`}</span>
                        </div>
                        <div className="progress ht-10">
                            <div
                                className={`progress-bar ${usageBarClass}`}
                                role="progressbar"
                                style={{ width: `${usagePercent}%` }}
                                aria-valuenow={usagePercent}
                                aria-valuemin="0"
                                aria-valuemax="100"
                            />
                        </div>
                    </div>

                    <div className="table-responsive">
                        <table className="table table-bordered mb-0 align-middle">
                            <thead>
                                <tr>
                                    <th>Tipo MIME</th>
                                    <th>Archivos</th>
                                    <th>Tamaño total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {stats.byMimeType.length === 0 && (
                                    <tr>
                                        <td colSpan={3} className="text-center text-muted py-3">No hay datos para mostrar.</td>
                                    </tr>
                                )}

                                {stats.byMimeType.map((item) => (
                                    <tr key={item.mimeType || 'unknown'}>
                                        <td>{item.mimeType || 'Sin tipo'}</td>
                                        <td>{Number(item.totalFiles || 0)}</td>
                                        <td>{formatBytes(item.totalBytes || 0)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
};
